import {useEffect, useMemo, useState} from 'react';
import {Form, Modal} from 'antd';
import {useMutation, useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import {
  CREATE_HELPDESK_CATEGORY,
  CREATE_HELPDESK_FEATURE,
  CREATE_HELPDESK_FEATURE_ISSUE,
  DELETE_HELPDESK_CATEGORY,
  DELETE_HELPDESK_FEATURE,
  DELETE_HELPDESK_FEATURE_ISSUE,
  GET_CONTEXT_RESOLVER_CATALOG,
  GET_HELPDESK_CATEGORIES,
  GET_HELPDESK_FEATURES,
  GET_HELPDESK_FEATURE_ISSUES,
  UPDATE_HELPDESK_CATEGORY,
  UPDATE_HELPDESK_FEATURE,
  UPDATE_HELPDESK_FEATURE_ISSUE,
} from 'graphql/queries/flowBuilder';
import {GET_ALL_AI_SKILLS} from '../resolvers';
import {CLOSED_FORM_MODAL} from './constants';
import {
  buildIssueInput,
  errorMessageFrom,
  issueRecordToFormValues,
  toSkillOptions,
  unwrap,
} from './utils';

const onErrorToast = {
  errorPolicy: 'all',
  onError: e => toast.error(e.message),
};

/* ---------------- helpers shared by the three submit handlers ---------------- */

const showConfirm = (title, content, onOk) =>
  Modal.confirm({title, content, onOk});

const showDeleteConfirm = (title, content, onOk) =>
  Modal.confirm({title, content, okType: 'danger', okText: 'Delete', onOk});

const unwrapMutationPayload = (res, mutationKey) => {
  const payload = res?.data?.[mutationKey];
  if (!payload) return {ok: false};
  if (payload.__typename === 'Error') {
    toast.error(payload.message);
    return {ok: false};
  }
  return {ok: true, payload};
};

/* ============================================================================= */

export const useFlowBuilder = () => {
  /* ---- selection state ---- */
  const [language, setLanguage] = useState('EN');
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedFeatureId, setSelectedFeatureId] = useState(null);

  /* ---- modal state + forms ---- */
  const [categoryModal, setCategoryModal] = useState(CLOSED_FORM_MODAL);
  const [featureModal, setFeatureModal] = useState(CLOSED_FORM_MODAL);
  const [issueModal, setIssueModal] = useState(CLOSED_FORM_MODAL);

  const [categoryForm] = Form.useForm();
  const [featureForm] = Form.useForm();
  const [issueForm] = Form.useForm();

  /* ---- queries ---- */
  const categoriesQuery = useQuery(GET_HELPDESK_CATEGORIES, {
    variables: {input: {language}},
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });
  const featuresQuery = useQuery(GET_HELPDESK_FEATURES, {
    variables: {input: {language, categoryId: selectedCategoryId}},
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    skip: !selectedCategoryId,
  });
  const issuesQuery = useQuery(GET_HELPDESK_FEATURE_ISSUES, {
    variables: {input: {language, featureId: selectedFeatureId}},
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    skip: !selectedFeatureId,
  });
  // Skills + resolver catalog only needed inside the Issue editor.
  const skillsQuery = useQuery(GET_ALL_AI_SKILLS, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
    skip: !issueModal.open,
  });
  const resolverCatalogQuery = useQuery(GET_CONTEXT_RESOLVER_CATALOG, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
    skip: !issueModal.open,
  });

  const categories = unwrap(categoriesQuery.data?.getHelpdeskCategories).rows;
  const features = unwrap(featuresQuery.data?.getHelpdeskFeatures).rows;
  const issues = unwrap(issuesQuery.data?.getHelpdeskFeatureIssues).rows;

  const categoriesError = errorMessageFrom(
    categoriesQuery.data,
    'getHelpdeskCategories',
    categoriesQuery.error,
  );
  const featuresError = errorMessageFrom(
    featuresQuery.data,
    'getHelpdeskFeatures',
    featuresQuery.error,
  );
  const issuesError = errorMessageFrom(
    issuesQuery.data,
    'getHelpdeskFeatureIssues',
    issuesQuery.error,
  );

  const skillOptions = useMemo(
    () => toSkillOptions(skillsQuery.data),
    [skillsQuery.data],
  );

  const resolverCatalog = useMemo(() => {
    const node = resolverCatalogQuery.data?.getContextResolverCatalog;
    return node?.__typename === 'ResponseWithContextResolverCatalog'
      ? node.data || []
      : [];
  }, [resolverCatalogQuery.data]);

  /* ---- mutations ---- */
  const [createCategory, {loading: creatingCat}] = useMutation(
    CREATE_HELPDESK_CATEGORY,
    onErrorToast,
  );
  const [updateCategory, {loading: updatingCat}] = useMutation(
    UPDATE_HELPDESK_CATEGORY,
    onErrorToast,
  );
  const [deleteCategory] = useMutation(DELETE_HELPDESK_CATEGORY, onErrorToast);

  const [createFeature, {loading: creatingFeat}] = useMutation(
    CREATE_HELPDESK_FEATURE,
    onErrorToast,
  );
  const [updateFeature, {loading: updatingFeat}] = useMutation(
    UPDATE_HELPDESK_FEATURE,
    onErrorToast,
  );
  const [deleteFeature] = useMutation(DELETE_HELPDESK_FEATURE, onErrorToast);

  const [createIssue, {loading: creatingIssue}] = useMutation(
    CREATE_HELPDESK_FEATURE_ISSUE,
    onErrorToast,
  );
  const [updateIssue, {loading: updatingIssue}] = useMutation(
    UPDATE_HELPDESK_FEATURE_ISSUE,
    onErrorToast,
  );
  const [deleteIssue] = useMutation(
    DELETE_HELPDESK_FEATURE_ISSUE,
    onErrorToast,
  );

  /* ---- keep selection valid when lists change ---- */
  useEffect(() => {
    if (!categories.length) {
      setSelectedCategoryId(null);
      return;
    }
    if (!categories.some(c => c.id === selectedCategoryId)) {
      setSelectedCategoryId(categories[0].id);
    }
  }, [categories, selectedCategoryId]);

  useEffect(() => {
    if (!features.length) {
      setSelectedFeatureId(null);
      return;
    }
    if (!features.some(f => f.id === selectedFeatureId)) {
      setSelectedFeatureId(features[0].id);
    }
  }, [features, selectedFeatureId]);

  /* ---- Category handlers ---- */
  const openCreateCategory = () => {
    categoryForm.resetFields();
    categoryForm.setFieldsValue({
      sortOrder: 1,
      isActive: true,
      iconUrl: '',
      availableToAnonymous: false,
    });
    setCategoryModal({open: true, mode: 'create', record: null});
  };
  const openEditCategory = record => {
    categoryForm.setFieldsValue({
      name: record.name,
      iconUrl: record.iconUrl || '',
      sortOrder: record.sortOrder ?? 0,
      isActive: !!record.isActive,
      availableToAnonymous: !!record.availableToAnonymous,
    });
    setCategoryModal({open: true, mode: 'edit', record});
  };
  const submitCategory = values => {
    const isEdit = categoryModal.mode === 'edit';
    showConfirm(
      isEdit ? 'Update category?' : 'Create category?',
      'Are you sure?',
      async () => {
        const trimmedIcon = (values.iconUrl || '').trim();
        const input = {
          ...(isEdit ? {id: categoryModal.record.id} : {language}),
          name: values.name,
          sortOrder: Math.max(1, Number(values.sortOrder) || 1),
          isActive: !!values.isActive,
          availableToAnonymous: !!values.availableToAnonymous,
          // On create: omit when empty. On edit: send '' to clear an existing icon.
          ...(trimmedIcon
            ? {iconUrl: trimmedIcon}
            : isEdit
            ? {iconUrl: ''}
            : {}),
        };
        const res = await (isEdit ? updateCategory : createCategory)({
          variables: {input},
        });
        const {ok, payload} = unwrapMutationPayload(
          res,
          isEdit ? 'updateHelpdeskCategory' : 'createHelpdeskCategory',
        );
        if (!ok) return;
        toast.success(payload.message || 'Saved');
        setCategoryModal(CLOSED_FORM_MODAL);
        categoriesQuery.refetch();
      },
    );
  };
  const confirmDeleteCategory = record => {
    showDeleteConfirm(
      `Delete "${record.name}"?`,
      'Deleting a category with features/issues may cascade. This action cannot be undone.',
      async () => {
        const res = await deleteCategory({variables: {input: {id: record.id}}});
        const {ok} = unwrapMutationPayload(res, 'deleteHelpdeskCategory');
        if (!ok) return;
        toast.success('Category deleted');
        categoriesQuery.refetch();
      },
    );
  };

  /* ---- Feature handlers ---- */
  const openCreateFeature = () => {
    if (!selectedCategoryId) return toast.warn('Select a category first');
    featureForm.resetFields();
    featureForm.setFieldsValue({
      sortOrder: 1,
      isActive: true,
      iconUrl: '',
      availableToAnonymous: false,
    });
    setFeatureModal({open: true, mode: 'create', record: null});
  };
  const openEditFeature = record => {
    featureForm.setFieldsValue({
      name: record.name,
      iconUrl: record.iconUrl || '',
      sortOrder: record.sortOrder ?? 0,
      isActive: !!record.isActive,
      availableToAnonymous: !!record.availableToAnonymous,
    });
    setFeatureModal({open: true, mode: 'edit', record});
  };
  const submitFeature = values => {
    const isEdit = featureModal.mode === 'edit';
    showConfirm(
      isEdit ? 'Update feature?' : 'Create feature?',
      'Are you sure?',
      async () => {
        const trimmedIcon = (values.iconUrl || '').trim();
        const base = {
          name: values.name,
          sortOrder: Math.max(1, Number(values.sortOrder) || 1),
          isActive: !!values.isActive,
          availableToAnonymous: !!values.availableToAnonymous,
          // On create: omit when empty. On edit: send '' to clear an existing icon.
          ...(trimmedIcon
            ? {iconUrl: trimmedIcon}
            : isEdit
            ? {iconUrl: null}
            : {}),
        };
        const input = isEdit
          ? {id: featureModal.record.id, ...base}
          : {categoryId: selectedCategoryId, language, ...base};
        const res = await (isEdit ? updateFeature : createFeature)({
          variables: {input},
        });
        const {ok, payload} = unwrapMutationPayload(
          res,
          isEdit ? 'updateHelpdeskFeature' : 'createHelpdeskFeature',
        );
        if (!ok) return;
        toast.success(payload.message || 'Saved');
        setFeatureModal(CLOSED_FORM_MODAL);
        featuresQuery.refetch();
      },
    );
  };
  const confirmDeleteFeature = record => {
    showDeleteConfirm(
      `Delete "${record.name}"?`,
      'Deleting a feature with issues may cascade.',
      async () => {
        const res = await deleteFeature({variables: {input: {id: record.id}}});
        const {ok} = unwrapMutationPayload(res, 'deleteHelpdeskFeature');
        if (!ok) return;
        toast.success('Feature deleted');
        featuresQuery.refetch();
      },
    );
  };

  /* ---- Issue handlers ---- */
  const openCreateIssue = () => {
    if (!selectedFeatureId) return toast.warn('Select a feature first');
    issueForm.resetFields();
    issueForm.setFieldsValue({
      stepTemplate: 'SIMPLE',
      sortOrder: 1,
      isActive: true,
      availableToAnonymous: false,
      fallthrough: false,
      faqPostIds: [],
      contextConfig: [],
    });
    setIssueModal({open: true, mode: 'create', record: null});
  };
  const openEditIssue = record => {
    issueForm.setFieldsValue(issueRecordToFormValues(record));
    setIssueModal({open: true, mode: 'edit', record});
  };
  const submitIssue = values => {
    const isEdit = issueModal.mode === 'edit';

    // DIRECT issues must not carry FAQs (server also enforces).
    if (values.stepTemplate === 'DIRECT' && (values.faqPostIds || []).length) {
      Modal.error({
        title: 'Validation',
        content: 'DIRECT issues cannot have any FAQ post-ids attached.',
      });
      return;
    }

    let input;
    try {
      input = buildIssueInput({
        values,
        isEdit,
        recordId: issueModal.record?.id,
        featureId: selectedFeatureId,
        language,
      });
    } catch (e) {
      Modal.error({title: 'Invalid input', content: e.message});
      return;
    }

    showConfirm(
      isEdit ? 'Update issue?' : 'Create issue?',
      'Are you sure?',
      async () => {
        const res = await (isEdit ? updateIssue : createIssue)({
          variables: {input},
        });
        const {ok, payload} = unwrapMutationPayload(
          res,
          isEdit ? 'updateHelpdeskFeatureIssue' : 'createHelpdeskFeatureIssue',
        );
        if (!ok) return;
        toast.success(payload.message || 'Saved');
        setIssueModal(CLOSED_FORM_MODAL);
        issuesQuery.refetch();
      },
    );
  };
  const confirmDeleteIssue = record => {
    showDeleteConfirm(`Delete "${record.title}"?`, null, async () => {
      const res = await deleteIssue({variables: {input: {id: record.id}}});
      const {ok} = unwrapMutationPayload(res, 'deleteHelpdeskFeatureIssue');
      if (!ok) return;
      toast.success('Issue deleted');
      issuesQuery.refetch();
    });
  };

  const refreshAll = () => {
    categoriesQuery.refetch();
    if (selectedCategoryId) featuresQuery.refetch();
    if (selectedFeatureId) issuesQuery.refetch();
  };

  return {
    // selection
    language,
    setLanguage,
    selectedCategoryId,
    setSelectedCategoryId,
    selectedFeatureId,
    setSelectedFeatureId,

    // data
    categories,
    features,
    issues,
    categoriesError,
    featuresError,
    issuesError,
    categoriesLoading: categoriesQuery.loading,
    featuresLoading: featuresQuery.loading,
    issuesLoading: issuesQuery.loading,
    skillOptions,
    skillsLoading: skillsQuery.loading,
    resolverCatalog,
    resolverCatalogLoading: resolverCatalogQuery.loading,
    refreshAll,

    // modals + forms
    categoryModal,
    setCategoryModal,
    featureModal,
    setFeatureModal,
    issueModal,
    setIssueModal,
    categoryForm,
    featureForm,
    issueForm,
    creatingCat,
    updatingCat,
    creatingFeat,
    updatingFeat,
    creatingIssue,
    updatingIssue,

    // handlers
    openCreateCategory,
    openEditCategory,
    submitCategory,
    confirmDeleteCategory,
    openCreateFeature,
    openEditFeature,
    submitFeature,
    confirmDeleteFeature,
    openCreateIssue,
    openEditIssue,
    submitIssue,
    confirmDeleteIssue,
  };
};
